import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { adminBrandAPI } from '../services/api';
import '../styles/Modal.css';

const BrandModal = ({ mode, brand, onClose, onSaved }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    if (mode === 'edit' && brand) {
      setFormData({
        name: brand.name || '',
        description: brand.description || ''
      });
    }
  }, [mode, brand]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Brand name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Brand name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Brand name cannot exceed 100 characters';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setServerError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let response;
      if (mode === 'create') {
        response = await adminBrandAPI.create(formData);
      } else {
        response = await adminBrandAPI.update(brand._id, formData);
      }

      if (response.data.success) {
        onSaved();
      } else {
        setServerError(response.data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Brand save error:', error);
      const message = error.response?.data?.message || 'An error occurred';
      setServerError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>{mode === 'create' ? 'Add New Brand' : 'Edit Brand'}</h2>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {serverError && (
            <div className="error-alert">{serverError}</div>
          )}

          <div className="form-group">
            <label htmlFor="name">Brand Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'error' : ''}
              disabled={loading}
              placeholder="Enter brand name"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={errors.description ? 'error' : ''}
              disabled={loading}
              placeholder="Enter brand description (optional)"
              rows="4"
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
            <small className="character-count">
              {formData.description.length}/500 characters
            </small>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Create Brand' : 'Update Brand'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrandModal;
